import { createRef, useEffect, useRef, useState } from 'react';
import {
  AudioMessage,
  AudioMessageRequest,
  ChatMessage,
  ChatRoomResponse,
  JoinRequest,
  LeaveRequest,
  LoadingMessage,
  RoomStageInfo,
  TextMessage,
  TextMessageRequest,
  User,
} from '../../../common/types';
import { ChatBar } from '../../../components/chat/ChatBar';
import ChatBox from '../../../components/chat/ChatBox';
import { messageTransition } from '../../../common/chat/transition';
import { useColors } from '../../../common/chat/userColors';
import { useAuthors } from '../../../common/chat/authors';
import { BackButton } from '../../../components/BackButton';
import { InfoButton } from '../../../components/InfoButton';
import { useSocket } from '../../../common/api/socket';

export type ChatViewProps = {
  roomUuid: string;
  room: ChatRoomResponse;
  user: User;
};

export function ChatView(props: ChatViewProps) {
  const { roomUuid, room, user } = props;
  const [stages, setStages] = useState<RoomStageInfo[]>(room.stages);
  const [members, setMembers] = useState<User[]>(room.members);
  const [messages, setMessages] = useState<ChatMessage[]>(room.messages);
  const [loading, setLoading] = useState<boolean>(room.loading);

  const chatContentRef = createRef<HTMLDivElement>();
  const prevChatScrollEnd = useRef<number>(0);
  const [firstScroll, setFirstScroll] = useState<boolean>(false);
  const barRef = useRef<HTMLTextAreaElement | null>(null);

  const socket = useSocket();

  useEffect(() => {
    if (!user) {
      return;
    }
    console.log(`Loading room ${roomUuid}`);
    setMembers(room.members);
    setMessages(room.messages);
    setFirstScroll(true);
    setLoading(room.loading);

    setStages(room.stages);
    if (!socket) {
      return;
    }
    // Fetch chat messages for the room
    // set up listening for new messages
    console.log('listening for messages');
    const joinRequest: JoinRequest = {
      roomUuid,
      user,
    };
    socket.removeAllListeners('chat message');
    socket.removeAllListeners('join room');
    socket.removeAllListeners('leave room');
    socket.emit('join room', joinRequest);
    socket.on('join room', (joinedUser: User) => {
      // make sure if the user is already in the room, we don't add them again
      setMembers((prevMembers) => [
        ...prevMembers.filter((m) => m.userUuid !== joinedUser.userUuid),
        joinedUser,
      ]);
      console.log('new member', joinedUser.name);
    });
    socket.on('leave room', (leftUser: User) => {
      // if the user is not in the room, nothing happens
      setMembers((prevMembers) => prevMembers.filter((m) => m.userUuid !== leftUser.userUuid));
      console.log('leaving member', leftUser.name);
    });
    socket.on('chat message', (message: ChatMessage) => {
      if (message.type !== 'text_delta') {
        console.log(message);
      }
      messageTransition(setMessages, setLoading, message, setStages);
    });
    setMembers((prevMembers) => [...prevMembers.filter((m) => m.userUuid !== user.userUuid), user]);
  }, [roomUuid, room, user, socket]);

  useEffect(() => {
    if (!socket || !user) {
      return;
    }
    const handleBeforeUnload = () => {
      const leaveRequest: LeaveRequest = {
        roomUuid,
        user,
      };
      console.log('leaving room');
      socket.emit('leave room', leaveRequest);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [socket, roomUuid, user]);

  function scrollToBottom() {
    if (chatContentRef.current) {
      const height = chatContentRef.current.clientHeight;
      const scrollHeight = chatContentRef.current.scrollHeight;
      const scrollEnd = scrollHeight - height;
      chatContentRef.current.scrollTop = scrollEnd;
      prevChatScrollEnd.current = scrollEnd;
    }
  }

  useEffect(() => {
    if (firstScroll && chatContentRef.current) {
      const height = chatContentRef.current.clientHeight;
      const scrollHeight = chatContentRef.current.scrollHeight;
      const scrollEnd = scrollHeight - height;
      chatContentRef.current.scrollTop = scrollEnd;
      prevChatScrollEnd.current = scrollEnd;
      console.log('first scroll');
      setFirstScroll(false);
    }
  }, [firstScroll, loading, chatContentRef, prevChatScrollEnd]);

  async function addMessage(message: string) {
    if (!user) {
      return;
    }
    if (!socket) {
      return;
    }
    setLoading(true);
    const textMessage: TextMessage = {
      type: 'text',
      roomUuid,
      content: message.trim(),
      author_type: 'user',
      user,
      createdAt: new Date(),
    };
    const loadingMessage: LoadingMessage = {
      type: 'loading',
      roomUuid,
      author_type: 'system',
      createdAt: new Date(),
    };
    const textReq: TextMessageRequest = {
      ...textMessage,
      user,
    };
    socket.emit('chat message', textReq);
    setMessages((prevMessages) => [...prevMessages, textMessage, loadingMessage]);
    scrollToBottom();
  }
  async function addAudio(audio: Blob, mimeType: string) {
    if (!user) {
      return;
    }
    if (!socket) {
      return;
    }
    setLoading(true);
    const content = await audio.arrayBuffer();
    const audioMessage: AudioMessage = {
      type: 'audio',
      roomUuid,
      content,
      mimeType,
      author_type: 'user',
      user,
      createdAt: new Date(),
    };
    const loadingMessage: LoadingMessage = {
      type: 'loading',
      roomUuid,
      author_type: 'system',
      createdAt: new Date(),
    };
    const audioReq: AudioMessageRequest = {
      ...audioMessage,
      user,
    };
    socket.emit('chat audio', audioReq);
    setMessages((prevMessages) => [...prevMessages, audioMessage, loadingMessage]);
    scrollToBottom();
  }

  useEffect(() => {
    if (chatContentRef.current) {
      const currentScroll = chatContentRef.current.scrollTop;
      const height = chatContentRef.current.clientHeight;
      const scrollHeight = chatContentRef.current.scrollHeight;
      const scrollEnd = scrollHeight - height;
      const prevScrollEnd = !prevChatScrollEnd.current ? scrollEnd : prevChatScrollEnd.current;

      const scrollEndTolerance = 100;
      if (currentScroll !== scrollEnd) {
        // console.log(`scroll ${currentScroll} -> ${prevScrollEnd} ? (${currentScroll + scrollEndTolerance} >= ${prevScrollEnd} -> ${currentScroll + scrollEndTolerance >= prevScrollEnd})`);
        if (currentScroll + scrollEndTolerance >= prevScrollEnd) {
          chatContentRef.current.scrollTop = scrollEnd;
        }
      }
      prevChatScrollEnd.current = scrollEnd;
    }
  }, [messages, chatContentRef]);

  const messageAuthors = useAuthors(messages);
  // Can't just use members, because people can join and leave. Don't like graying out the names of people who left.
  // But also not the worst idea.
  const authorColors = useColors([...messageAuthors, ...members]);

  return (
    <div className="home-page bg-[#101c2c] text-white font-sans flex flex-col h-dvh fade-in-component">
      <BackButton scenarioUuid={room.scenarioUuid} />
      <InfoButton
        room={{
          number: room.number,
          uuid: roomUuid,
          size: room.size,
          members,
          stages,
        }}
        messageAuthors={messageAuthors}
        authorColors={authorColors}
      />

      <div className="flex flex-col flex-grow min-h-0">
        <div ref={chatContentRef} className="flex-grow overflow-y-auto overflow-x-hidden">
          <div className="mb-4" />
          <ChatBox
            messages={messages}
            authorColors={authorColors}
            scrollToBottom={scrollToBottom}
            stages={stages}
          />
          <div className="mb-8" />
        </div>
        <div className="px-4 pb-4 bg-transparent">
          <div className="mx-auto lg:max-w-4xl bg-transparent">
            <ChatBar
              addMessage={addMessage}
              addAudio={addAudio}
              loading={loading}
              textareaRef={barRef}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
